// cesium.ts - Main Cesium implementation file

import {
  CameraEventType,
  Cartesian3,
  Math as CesiumMath,
  Color,
  Ion,
  JulianDate,
  PolygonHierarchy,
  RequestScheduler,
  Resource,
  Terrain,
  Viewer,
} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";

import { polygon, union } from "@turf/turf";
import {
  ColorGeometryInstanceAttribute,
  GeoJsonDataSource,
  GeometryInstance,
  GroundPolylineGeometry,
  GroundPolylinePrimitive,
  GroundPrimitive,
  PolygonGeometry,
  PolylineColorAppearance,
} from "cesium";
import {
  findAssetById as _findAssetById,
  loadAndZoomToAsset as _loadAndZoomToAsset,
  removeAssetById as _removeAssetById,
  zoomToAssetById as _zoomToAssetById,
  loadMultipleTilesets,
  setupZoomToTileset,
  tilesetRegistry,
} from "./cesium-assets";
import {
  flyToMapbounds as _flyToMapbounds,
  flyToPosition as _flyToPosition,
  flyToView as _flyToView,
  getCurrentCameraPosition as _getCurrentCameraPosition,
  setCameraPosition as _setCameraPosition,
  updateCameraPosition as _updateCameraPosition,
  createCameraOptions,
  resetCameraState,
  setCameraReady,
  setupCameraEvents,
} from "./cesium-camera";
import {
  setBimHighlightingEnabled,
  setupBimHighlightingForExistingTileset,
  setupClickHandlers,
  setupGlobalEvents,
  setupHoverHandlers,
} from "./cesium-events";
import { getSimpleGeoidUndulation } from "./cesium-geoid";
import { initializeGeoidWithOffset } from "./cesium-geoid-init";
import { changeImageryProvider } from "./cesium-imagery";
import { applyInitialStyles } from "./cesium-styling";
import type {
  CesiumViewSettings,
  InitializeCesiumOptions,
  StyleCondition,
} from "./cesium-types";

// Set Cesium Ion access token

Ion.defaultAccessToken = import.meta.env.VITE_CESIUM_ACCESS_TOKEN;

// Configure Cesium resource handling and request scheduling for performance
(Resource as any).retryAttempts = 0; // No retries for faster feedback
(Resource as any).retryCallback = undefined;

// Simple request scheduling optimization for faster asset loading
if (
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
) {
  // Conservative mobile settings to prevent crashes
  RequestScheduler.maximumRequestsPerServer = 4; // Lowered for mobile stability
  RequestScheduler.maximumRequests = 8; // Low total for mobile
} else {
  // Desktop can handle more aggressive settings
  RequestScheduler.maximumRequestsPerServer = 8;
  RequestScheduler.maximumRequests = 32;
}

// Global viewer instance
let viewer: any = null;
let isMobileDevice = false;

// Utility functions
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

// Configure viewer options based on device type and Google Photorealistic usage
function createViewerOptions(
  isMobile: boolean,
  enablePerformanceMode = false,
  preserveDrawingBuffer,
): any {
  const baseOptions = {
    // Always use world terrain during construction to avoid init errors
    terrain: Terrain.fromWorldTerrain(),
    // Note: Will switch to ellipsoid and remove imagery after creation if Google is used
    navigationHelpButton: false,
    homeButton: false,
    geocoder: false,
    timeline: false,
    fullscreenButton: false,
    animation: false,
    sceneModePicker: false,
    baseLayerPicker: false,
    infoBox: false,
    targetFrameRate: isMobile ? 30 : 60, // Lowered frame rate for mobile stability
    scene3DOnly: true, // Performance: Render for 3D only
    requestRenderMode: true, // Only render when needed
    maximumRenderTimeChange: Number.POSITIVE_INFINITY,
    orderIndependentTranslucency: false, // Performance: Disable OIT for speed
    // useBrowserRecommendedResolution: !enablePerformanceMode, // Performance: Use CSS pixels on high-DPI devices
    useBrowserRecommendedResolution: false,
    // Aggressive performance optimizations
    shadows: false, // Disable shadows (expensive)
    terrainShadows: false,
    tileCacheSize: 50, // Limit tile cache size

    msaaSamples: enablePerformanceMode ? 1 : 4, // Reduce MSAA samples in performance mode

    contextOptions: {
      webgl: {
        alpha: false,
        antialias: !enablePerformanceMode, // Disable antialiasing for performance
        powerPreference: "low-power", // Prioritize performance over quality
        depth: true,
        stencil: false,
        premultipliedAlpha: true,
        preserveDrawingBuffer: preserveDrawingBuffer,
        failIfMajorPerformanceCaveat: false,
      },
    },
  };

  return baseOptions;
}

// Configure scene after viewer creation
function configureSceneAfterCreation(viewer: any) {
  // Set sky atmosphere and lighting for consistent model illumination

  viewer.scene.skyAtmosphere.show = true;
  viewer.scene.globe.enableLighting = true; // Enable lighting for consistent illumination
  viewer.scene.sun.show = true;
  viewer.scene.sun.glowFactor = 1.0; // Reduced from 10 for more realistic lighting
  viewer.scene.moon.show = false;

  // Enable image-based lighting for better 3D model illumination
  viewer.scene.sphericalHarmonicCoefficients = undefined; // Use default atmospheric lighting
  viewer.scene.specularEnvironmentMaps = undefined; // Use default environment maps
  viewer.scene.globe.depthTestAgainstTerrain = false; // Enable depth testing to prevent z-fighting
  // viewer.scene.globe.maximumScreenSpaceError = 5;
  // viewer.scene.globe.tileCacheSize = 1000; // default 100
  viewer.scene.globe.maximumScreenSpaceError = isMobileDevice ? 1 : 1; // Increased for better performance
  viewer.scene.globe.tileCacheSize = isMobileDevice ? 300 : 800; // Reduced cache size

  // Additional performance optimizations for the globe
  viewer.scene.globe.preloadSiblings = false; // Don't preload sibling tiles unless needed
  viewer.scene.globe.preloadAncestors = false; // Don't preload ancestor tiles unless needed
  viewer.scene.globe.loadingDescendantLimit = isMobileDevice ? 10 : 20; // Limit concurrent loads

  // Set background colors
  viewer.scene.backgroundColor = Color.fromCssColorString("#f9f8f7");
  viewer.scene.globe.baseColor =
    Color.fromCssColorString("#f9f8f7").withAlpha(0);

  viewer.scene.skyBox.show = false;

  viewer.scene.postProcessStages.fxaa.enabled = true;

  // Set appropriate screen space error based on device type - this is the correct place for this setting
  // viewer.scene.globe.maximumScreenSpaceError = isMobileDevice ? 16 : 16; // Reverted to safe value to prevent crashes

  // Apply aggressive LOD settings for mobile performance
  if (isMobileDevice) {
    // More aggressive LOD settings for mobile to improve performance
    viewer.scene.globe.skipLevelOfDetail = true;

    // High skip screen space error factor for aggressive skipping
    viewer.scene.globe.skipScreenSpaceErrorFactor = 16;

    // Skip multiple detail levels
    viewer.scene.globe.skipLevels = 2;

    // Conservative mobile optimizations for stability

    // Apply lowered resolution scale for better performance
    viewer.resolutionScale = 0.75;
  }

  // Ensure proper render mode for consistent performance
  // viewer.scene.requestRenderMode = true;
  // viewer.scene.maximumRenderTimeChange = Number.POSITIVE_INFINITY;
}

// Function to set the scene's time for consistent lighting
function setSceneTime(viewer: any) {
  // Set the time to 14:00 on June 21st (summer solstice) for consistent, bright lighting
  // This provides optimal sun angle for 3D model illumination throughout the year
  const summerSolstice = new Date();
  summerSolstice.setUTCFullYear(2024, 5, 21); // June 21st, 2024 (month 5 = June, 0-indexed)
  summerSolstice.setUTCHours(12, 0, 0, 0); // 12:00 UTC = 14:00 CEST (optimal sun angle)
  viewer.clock.currentTime = JulianDate.fromDate(summerSolstice);

  // Disable clock animation to keep lighting consistent
  viewer.clock.shouldAnimate = false;
  viewer.scene.requestRender(); // Ensure the scene re-renders with the new time
}

// Setup camera controls
function setupCameraControls(viewer: any) {
  viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1.0;
  const controller = viewer.scene.screenSpaceCameraController;

  if (isMobileDevice) {
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  } else {
    controller.tiltEventTypes = [CameraEventType.RIGHT_DRAG];
    controller.rotateEventTypes = [CameraEventType.LEFT_DRAG];
    controller.zoomEventTypes = [CameraEventType.WHEEL, CameraEventType.PINCH];
    controller.translateEventTypes = [CameraEventType.MIDDLE_DRAG];
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  }

  // Disable inertia for immediate response and better performance
  controller.inertiaSpin = 0.0;
  controller.inertiaTranslate = 0.0;
  controller.inertiaZoom = 0.0;
}

// Function to create visual polygon mask for Cesium using GeoJsonDataSource with clampToGround
async function setupCesiumPolygonMask(viewer: any, mapbounds: any) {
  if (!mapbounds) {
    console.warn("No mapbounds provided for polygon mask");
    return;
  }

  // Guard against multiple calls
  if (viewer._creatingPolygonMask) {
    console.warn("⚠️  Polygon mask creation already in progress, skipping...");
    return;
  }
  viewer._creatingPolygonMask = true;

  try {
    // Parse mapbounds if it's a string
    const geoData =
      typeof mapbounds === "string" ? JSON.parse(mapbounds) : mapbounds;

    if (geoData && geoData.features) {
      // Calculate clipping bounds for the mask area (100km padding)
      let minLng = Number.POSITIVE_INFINITY,
        minLat = Number.POSITIVE_INFINITY,
        maxLng = Number.NEGATIVE_INFINITY,
        maxLat = Number.NEGATIVE_INFINITY;

      // Get bounds from all features in mapbounds
      geoData.features.forEach((boundFeature: any) => {
        if (boundFeature.geometry && boundFeature.geometry.coordinates) {
          const { type, coordinates } = boundFeature.geometry;
          if (type === "Polygon") {
            coordinates[0].forEach((coord: number[]) => {
              minLng = Math.min(minLng, coord[0]);
              maxLng = Math.max(maxLng, coord[0]);
              minLat = Math.min(minLat, coord[1]);
              maxLat = Math.max(maxLat, coord[1]);
            });
          } else if (type === "MultiPolygon") {
            coordinates.forEach((polygonCoords: number[][][]) => {
              polygonCoords[0].forEach((coord: number[]) => {
                minLng = Math.min(minLng, coord[0]);
                maxLng = Math.max(maxLng, coord[0]);
                minLat = Math.min(minLat, coord[1]);
                maxLat = Math.max(maxLat, coord[1]);
              });
            });
          }
        }
      });

      // Add ~100km padding in all directions
      const kmPadding = 1000;
      const degreesPadding = kmPadding / 111;
      const lonPadding = degreesPadding;
      const latPadding = degreesPadding;

      const clippingBounds = {
        west: minLng - lonPadding,
        south: minLat - latPadding,
        east: maxLng + lonPadding,
        north: maxLat + latPadding,
      };

      // But use clipping bounds instead of world bounds
      const clippingPolygon = [
        [clippingBounds.west, clippingBounds.south],
        [clippingBounds.east, clippingBounds.south],
        [clippingBounds.east, clippingBounds.north],
        [clippingBounds.west, clippingBounds.north],
        [clippingBounds.west, clippingBounds.south],
      ];

      // Create polygon geometry using GroundPrimitive for proper terrain clamping
      const maskOuterRing = clippingPolygon.map((coord: number[]) =>
        Cartesian3.fromDegrees(coord[0], coord[1]),
      );

      // Create holes from project areas
      const holes: PolygonHierarchy[] = [];
      geoData.features.forEach((feature: any) => {
        if (feature.geometry && feature.geometry.coordinates) {
          const { type, coordinates } = feature.geometry;
          if (type === "Polygon") {
            const holePositions = coordinates[0].map((coord: number[]) =>
              Cartesian3.fromDegrees(coord[0], coord[1]),
            );
            holes.push(new PolygonHierarchy(holePositions));
          } else if (type === "MultiPolygon") {
            coordinates.forEach((polygonCoords: number[][][]) => {
              const holePositions = polygonCoords[0].map((coord: number[]) =>
                Cartesian3.fromDegrees(coord[0], coord[1]),
              );
              holes.push(new PolygonHierarchy(holePositions));
            });
          }
        }
      });

      // Create PolygonGeometry with holes
      const polygonGeometry = new PolygonGeometry({
        polygonHierarchy: new PolygonHierarchy(maskOuterRing, holes),
        height: 0,
        perPositionHeight: false, // Critical for ground clamping
      });

      // Create GeometryInstance
      const geometryInstance = new GeometryInstance({
        geometry: polygonGeometry,
        attributes: {
          color: ColorGeometryInstanceAttribute.fromColor(
            Color.WHITE.withAlpha(0.7),
          ),
        },
      });

      // Use GroundPrimitive for proper terrain clamping
      const groundPrimitive = new GroundPrimitive({
        geometryInstances: geometryInstance,
        asynchronous: true,
      });

      viewer.scene.primitives.add(groundPrimitive);

      // Create merged outline using Turf.js union functionality
      const groundPolylineInstances: GeometryInstance[] = [];

      // Convert features to Turf polygons and merge them
      const initialPolygons = geoData.features?.flatMap((feature: any) => {
        const { type, coordinates } = feature.geometry;

        if (type === "Polygon") {
          return [polygon(coordinates)];
        }
        if (type === "MultiPolygon") {
          // For multipolygons, create a polygon for each part
          return coordinates.map((polygonCoords: number[][][]) =>
            polygon(polygonCoords),
          );
        }
        return [];
      });

      if (initialPolygons && initialPolygons.length > 0) {
        // Merge all polygons into one using Turf union
        let combinedPolygon = initialPolygons[0];

        for (let i = 1; i < initialPolygons.length; i++) {
          combinedPolygon = union(combinedPolygon, initialPolygons[i]);
        }

        // Create outline from the merged polygon
        if (combinedPolygon && combinedPolygon.geometry) {
          const { type, coordinates } = combinedPolygon.geometry;

          if (type === "Polygon") {
            const cartesianPositions = coordinates[0].map((coord: number[]) =>
              Cartesian3.fromDegrees(coord[0], coord[1]),
            );

            // Close the loop by adding the first point at the end
            const closedPositions = [
              ...cartesianPositions,
              cartesianPositions[0],
            ];

            const groundPolylineGeometry = new GroundPolylineGeometry({
              positions: closedPositions,
              width: 3.0,
            });

            const instance = new GeometryInstance({
              geometry: groundPolylineGeometry,
              attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(
                  Color.fromCssColorString("#FFD061"),
                ),
              },
            });

            groundPolylineInstances.push(instance);
          } else if (type === "MultiPolygon") {
            // Handle case where union results in MultiPolygon (multiple separate areas)
            coordinates.forEach((polygonCoords: number[][][]) => {
              const cartesianPositions = polygonCoords[0].map(
                (coord: number[]) => Cartesian3.fromDegrees(coord[0], coord[1]),
              );

              // Close the loop by adding the first point at the end
              const closedPositions = [
                ...cartesianPositions,
                cartesianPositions[0],
              ];

              const groundPolylineGeometry = new GroundPolylineGeometry({
                positions: closedPositions,
                width: 3.0,
              });

              const instance = new GeometryInstance({
                geometry: groundPolylineGeometry,
                attributes: {
                  color: ColorGeometryInstanceAttribute.fromColor(
                    Color.fromCssColorString("#FFD061"),
                  ),
                },
              });

              groundPolylineInstances.push(instance);
            });
          }
        }
      }

      // Create GroundPolylinePrimitive for terrain-clamped outlines
      let groundPolylinePrimitive = null;
      if (groundPolylineInstances.length > 0) {
        groundPolylinePrimitive = new GroundPolylinePrimitive({
          geometryInstances: groundPolylineInstances,
          appearance: new PolylineColorAppearance(),
          asynchronous: true,
        });

        viewer.scene.primitives.add(groundPolylinePrimitive);
      }

      // Store cleanup function for polygon mask
      viewer._polygonMaskCleanup = () => {
        viewer.scene.primitives.remove(groundPrimitive);
        if (groundPolylinePrimitive) {
          viewer.scene.primitives.remove(groundPolylinePrimitive);
        }
        viewer._creatingPolygonMask = false; // Reset the guard
      };
    }
  } catch (error) {
    console.error("Error creating Cesium polygon mask:", error);
    viewer._creatingPolygonMask = false; // Reset the guard on error
  } finally {
    viewer._creatingPolygonMask = false; // Always reset the guard
  }
}

// Function to restrict camera movement within mapbounds
function setupCameraBoundsRestriction(viewer: any, mapbounds: any) {
  if (!mapbounds) {
    console.warn("No mapbounds provided for camera restriction");
    return;
  }

  // Calculate bounds from mapbounds FeatureCollection
  let minLng = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;

  try {
    // Parse mapbounds if it's a string
    const geoData =
      typeof mapbounds === "string" ? JSON.parse(mapbounds) : mapbounds;

    if (geoData && geoData.features) {
      geoData.features.forEach((feature: any) => {
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;

          // Handle different geometry types (Polygon, MultiPolygon, etc.)
          const flattenCoords = (coordArray: any[]): number[][] => {
            if (
              Array.isArray(coordArray[0]) &&
              Array.isArray(coordArray[0][0])
            ) {
              return coordArray.flat();
            }
            return coordArray;
          };

          const allCoords = flattenCoords(coords);
          allCoords.forEach((coord: number[]) => {
            if (coord.length >= 2) {
              const [lng, lat] = coord;
              minLng = Math.min(minLng, lng);
              minLat = Math.min(minLat, lat);
              maxLng = Math.max(maxLng, lng);
              maxLat = Math.max(maxLat, lat);
            }
          });
        }
      });
    }
  } catch (error) {
    console.error("Error parsing mapbounds:", error);
    return;
  }

  // Add minimal padding to bounds (20% buffer for edge tolerance)
  const lonPadding = (maxLng - minLng) * 1;
  const latPadding = (maxLat - minLat) * 1;

  const restrictedBounds = {
    west: minLng - lonPadding,
    south: minLat - latPadding,
    east: maxLng + lonPadding,
    north: maxLat + latPadding,
  };

  // Create true wall effect by intercepting camera movements before they happen
  const controller = viewer.scene.screenSpaceCameraController;

  // Set height constraints using built-in Cesium controls
  controller.minimumZoomDistance = 50; // 50 meters minimum
  controller.maximumZoomDistance = 50000; // 50km maximum

  // Store original camera position to prevent movement outside bounds
  let lastValidPosition = viewer.camera.positionCartographic.clone();

  // Override camera update to create wall effect
  const originalUpdate = viewer.camera.update;
  viewer.camera.update = function (mode: any) {
    // Call original update first
    const result = originalUpdate.call(this, mode);

    // Check if camera moved outside bounds
    const currentPosition = this.positionCartographic;
    if (currentPosition) {
      const longitude = CesiumMath.toDegrees(currentPosition.longitude);
      const latitude = CesiumMath.toDegrees(currentPosition.latitude);

      // Check if current position is within bounds
      const withinBounds =
        longitude >= restrictedBounds.west &&
        longitude <= restrictedBounds.east &&
        latitude >= restrictedBounds.south &&
        latitude <= restrictedBounds.north;

      if (withinBounds) {
        // Update last valid position
        lastValidPosition = currentPosition.clone();
      } else {
        // Restore to last valid position - this creates the wall effect
        this.position = Cartesian3.fromRadians(
          lastValidPosition.longitude,
          lastValidPosition.latitude,
          lastValidPosition.height,
        );
      }
    }

    return result;
  };

  // Store cleanup function
  viewer._boundsRestrictionCleanup = () => {
    // Restore original camera behavior
    viewer.camera.update = originalUpdate;
    controller.minimumZoomDistance = 1.0;
    controller.maximumZoomDistance = Number.POSITIVE_INFINITY;
  };

  // Combine both cleanup functions
  const originalBoundsCleanup = viewer._boundsRestrictionCleanup;
  viewer._boundsRestrictionCleanup = () => {
    originalBoundsCleanup();
    if (viewer._polygonMaskCleanup) {
      viewer._polygonMaskCleanup();
    }
  };
}

// Main initialization function
export async function initializeCesium(
  options: InitializeCesiumOptions = {},
): Promise<void> {
  const {
    containerId = "cesiumContainer",
    onObjectClick,
    assetIds = [],
    bimAssetStyles,
    assetMetadataMap,
    mapData,
    imageryStyle,
    onCameraMove,
    onViewCenterChange,
    onReady,
    initialCameraPosition,
    autoZoomToAssets,
    clippingRectangle,
    mapbounds,
    cacheBytes,
    maximumCacheOverflowBytes,
    tileCacheSize,
    enableCameraBoundsRestriction = true,
    preserveDrawingBuffer = false,
  } = options;

  try {
    isMobileDevice = isMobile();
    const hasInitialCameraPosition = !!initialCameraPosition;

    // Check if container has valid dimensions before initializing Cesium
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`);
    }

    const containerRect = container.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) {
      throw new Error(
        `Container has zero dimensions: ${containerRect.width}x${containerRect.height}`,
      );
    }

    // Create and configure viewer
    const viewerOptions = createViewerOptions(
      isMobileDevice,
      shouldEnablePerformanceMode(),
      preserveDrawingBuffer,
    );
    viewer = new Viewer(containerId, viewerOptions);

    if (!viewer) {
      return;
    }

    // Store viewer globally for access from other modules
    (window as any).cesiumViewer = viewer;

    // Configure the scene with our settings
    configureSceneAfterCreation(viewer);

    viewer.scene.globe.tileCacheSize =
      tileCacheSize || (isMobileDevice ? 50 : 200);

    setSceneTime(viewer);

    // Enable terrain clamping globally for GeoJSON data
    GeoJsonDataSource.clampToGround = true;

    // Enable depth testing against terrain (from Cesium example)
    viewer.scene.globe.depthTestAgainstTerrain = false;

    // Don't remove imagery layers during initialization - let the toggle handle it
    // Setup camera controls (bounds restriction will be added later)
    setupCameraControls(viewer);

    // Setup zoom to tileset functionality
    setupZoomToTileset(viewer, hasInitialCameraPosition, autoZoomToAssets);

    // Setup event handlers
    setupCameraEvents(
      viewer,
      tilesetRegistry,
      onCameraMove,
      onViewCenterChange,
    );
    setupClickHandlers(viewer, onObjectClick);
    setupHoverHandlers(viewer, isMobileDevice);
    setupGlobalEvents(viewer, isMobileDevice);

    // Setup BIM feature highlighting (opt-in)
    if (options.enableBimHoverHighlight) {
      setBimHighlightingEnabled(!!options.enableBimHoverHighlight);
    }

    // Initialize geoid correction for BIM models (fixes underground model issues)
    if (
      mapData?.geoidHeightDifference !== undefined &&
      mapData?.geoidHeightDifference !== null
    ) {
      initializeGeoidWithOffset(mapData.geoidHeightDifference);
    } else {
      // Calculate expected geoid undulation for the map location
      let mapLon = 13.8609; // Default fallback
      let mapLat = 50.9866; // Default fallback

      // Use mapData coordinates if available
      if (
        mapData?.bbox &&
        Array.isArray(mapData.bbox) &&
        mapData.bbox.length >= 2
      ) {
        mapLon = mapData.bbox[0];
        mapLat = mapData.bbox[1];
      }
      // Fallback to initial camera position
      else if (initialCameraPosition?.center) {
        mapLon = initialCameraPosition.center[0];
        mapLat = initialCameraPosition.center[1];
      }
      // Fallback to first asset metadata if available
      else if (assetMetadataMap && assetMetadataMap.size > 0) {
        const firstAsset = Array.from(assetMetadataMap.values())[0];
        if (firstAsset.lat && firstAsset.lng) {
          mapLon = firstAsset.lng;
          mapLat = firstAsset.lat;
        }
      }

      const calculatedUndulation = getSimpleGeoidUndulation(mapLon, mapLat);
      initializeGeoidWithOffset(calculatedUndulation);
    }

    // Drawing is now handled by the UI component, not in the core library
    if (options.polygonDrawingEnabled) {
    }

    // Set initial camera position if provided (no animation for initial load)
    if (initialCameraPosition) {
      const cameraOptions = createCameraOptions(initialCameraPosition, 0.0); // 0 duration for initial load
      viewer.camera.flyTo(cameraOptions);

      // Show the globe after camera is positioned
      viewer._initialFlightStarted = true;
    }

    // Load initial assets
    const tilesets = await loadMultipleTilesets(
      viewer,
      isMobileDevice,
      assetIds,
      bimAssetStyles,
      assetMetadataMap,
      false, // Initially load all assets without camera filtering
      cacheBytes,
      maximumCacheOverflowBytes,
    );

    // Apply initial styles after tilesets are loaded
    applyInitialStyles(viewer, tilesetRegistry, bimAssetStyles);

    // Set up BIM highlighting for existing tilesets if enabled
    if (options.enableBimHoverHighlight && tilesets) {
      tilesets.forEach((tileset) => {
        if (tileset) {
          setupBimHighlightingForExistingTileset(tileset);
        }
      });
    }

    // Only auto-zoom to first asset if no initial camera position or autoZoomToAssets is not false
    // Use 0 duration for initial load to avoid flying animation from globe
    if (
      (!hasInitialCameraPosition || autoZoomToAssets !== false) &&
      assetIds.length > 0
    ) {
      const firstTileset = _findAssetById(viewer, assetIds[0]);
      if (firstTileset) {
        // Set duration to 0 for initial load
        const boundingSphere = firstTileset.boundingSphere;
        if (boundingSphere) {
          viewer.camera.flyToBoundingSphere(boundingSphere, {
            duration: 0.0,
          });
        } else {
          viewer.zoomToTileset(firstTileset, { duration: 0.0 });
        }
      }
    }

    // Apply initial imagery style if provided
    if (imageryStyle && imageryStyle !== "mapbox-outdoors") {
      setTimeout(() => {
        changeImageryProvider(
          viewer,
          imageryStyle,
          isMobileDevice,
          clippingRectangle,
          mapbounds,
        ).catch((error) => {});
      }, 100); // Reduced from 1000ms to 100ms for faster loading
    }

    // Setup camera bounds restriction and polygon mask after everything is initialized
    if (mapbounds) {
      setTimeout(() => {
        // Only set up camera bounds restriction if explicitly enabled
        if (enableCameraBoundsRestriction) {
          setupCameraBoundsRestriction(viewer, mapbounds);
        }

        // Clean up any existing polygon mask first
        if (viewer._polygonMaskCleanup) {
          viewer._polygonMaskCleanup();
          viewer._polygonMaskCleanup = null;
        }

        // Remove any entities with "Mask" in the name
        const entitiesToRemove = viewer.entities.values.filter(
          (entity: any) => entity.name && entity.name.includes("Mask"),
        );
        entitiesToRemove.forEach((entity: any) => {
          viewer.entities.remove(entity);
        });

        // Remove any data sources that might contain mask polygons
        const allDataSources = viewer.dataSources._dataSources.slice(); // Create a copy
        allDataSources.forEach((ds: any) => {
          if (ds.name && ds.name.includes("Mask")) {
            viewer.dataSources.remove(ds);
          }
          // Also check for any data sources that might contain polygon entities
          if (ds.entities) {
            const polygonEntities = ds.entities.values.filter(
              (entity: any) =>
                entity.polygon && entity.name && entity.name.includes("Mask"),
            );
            if (polygonEntities.length > 0) {
              viewer.dataSources.remove(ds);
            }
          }
        });

        // Create polygon mask directly (removed terrain waiting)
        setupCesiumPolygonMask(viewer, mapbounds);

        // Apply clipping to existing OSM buildings if they exist
        // applyOsmBuildingsClipping(mapbounds);
      }, 5000); // Wait 2 seconds for initial camera positioning to complete
    }

    // Mark as ready
    setCameraReady(true);

    onReady?.(viewer);
  } catch (error: any) {
    console.error("Error initializing Cesium:", error);
  }
}

// Helper function to determine if performance mode should be enabled
function shouldEnablePerformanceMode(): boolean {
  return isMobileDevice;
}

export function flyToPosition(
  viewerInstance: any,
  flyToSettings: CesiumViewSettings,
  cesiumCameraPosition?: CesiumViewSettings,
) {
  _flyToPosition(flyToSettings, cesiumCameraPosition, viewerInstance);
}

// Public API functions
export function getViewer() {
  return viewer;
}

export function resetViewerState() {
  resetCameraState();
  // We don't reset the viewer object itself to avoid breaking React state
}

export function flyToView(
  viewerInstance: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  _flyToView(viewerInstance, settings, duration, onComplete);
}

export function updateCameraPosition(
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  return _updateCameraPosition(viewer, settings, duration, onComplete);
}

export function setCameraPosition(settings: CesiumViewSettings) {
  return _setCameraPosition(viewer, settings);
}

export function getCurrentCameraPosition(): CesiumViewSettings | null {
  return _getCurrentCameraPosition(viewer);
}

// Public API asset functions that use the internal viewer
export function loadAndZoomToAsset(
  assetId: number,
  duration?: number,
  containerId?: string,
) {
  // If containerId is provided, we should get the viewer for that specific container
  // For now, using the global viewer (this is a limitation of the current architecture)
  return _loadAndZoomToAsset(viewer, assetId, duration);
}

export function zoomToAssetById(
  assetId: number,
  duration?: number,
  containerId?: string,
) {
  // If containerId is provided, we should get the viewer for that specific container
  // For now, using the global viewer (this is a limitation of the current architecture)
  return _zoomToAssetById(viewer, assetId, duration);
}

export function removeAssetById(assetId: number, viewerInstance?: any) {
  // If a containerId is provided, it's ignored for now as we assume a single viewer.
  // This could be enhanced to support multiple viewers if needed.
  return _removeAssetById(viewerInstance || viewer, assetId);
}

export function findAssetById(assetId: number) {
  return _findAssetById(viewer, assetId);
}

export function flyToMapbounds(mapbounds: any, duration = 2.0, pitch = -1.3) {
  return _flyToMapbounds(viewer, mapbounds, duration, pitch);
}

// Enhanced tileset loading with smart filtering (BIM assets only)
export function loadSmartTilesets(
  bimAssetIds: number[] = [],
  styles?: StyleCondition[],
  assetMetadataMap?: Map<number, { lat: number; lng: number; radius?: number }>,
  enableCameraFiltering = true,
) {
  if (!viewer) return;

  const isMobile = isMobileDevice;

  // Use the updated loadMultipleTilesets function
  loadMultipleTilesets(
    viewer,
    isMobile,
    bimAssetIds,
    styles,
    assetMetadataMap,
    enableCameraFiltering,
  );
}

// Re-export types
export type {
  CameraOptions,
  CesiumViewSettings,
  InitializeCesiumOptions,
  StyleCondition,
} from "./cesium-types";

// DOM event handlers for settings
document.addEventListener("DOMContentLoaded", () => {
  const depthTestCheckbox = document.getElementById(
    "toggleDepthTest",
  ) as HTMLInputElement;
  const shadowsCheckbox = document.getElementById(
    "toggleShadows",
  ) as HTMLInputElement;

  function updateSettings() {
    if (viewer) {
      viewer.scene.globe.depthTestAgainstTerrain = depthTestCheckbox.checked;
      viewer.shadows = shadowsCheckbox.checked;
    }
  }

  if (depthTestCheckbox && shadowsCheckbox) {
    depthTestCheckbox.addEventListener("change", updateSettings);
    shadowsCheckbox.addEventListener("change", updateSettings);
  }
});

export {
  cleanupHoverHandlers,
  setBimHighlightingEnabled,
  setupBimHighlightingForExistingTileset,
  setupBimHoverHighlighting,
} from "./cesium-events";
export { reapplyGeoidCorrectionToAllTilesets } from "./cesium-geoid";
// Consolidated exports
export {
  changeImageryProvider,
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "./cesium-imagery";
export { applyStylesToAllTilesets } from "./cesium-styling";
